import { useCallback, useEffect, useMemo, useState } from 'react'

import { ELECTIONS_ADDRESS, VOTE_ADDRESS } from '@/constants/blockchain'
import OMMAElection from '@/contracts/OMMAElection'
import Vote from '@/contracts/Vote'
import { useAuth } from '@/plugins/auth'
import { l1NativeWagmiConfig } from '@/plugins/auth/config'
import { CustomError, parseError } from '@/utils/helpers'
import { emptyBalance, processBalance, ProcessedBalance } from '@/utils/utils'

import { useCustomContractRead, useCustomContractReadBalance } from './useCustomContract'
import {
  fetchBalance,
  fetchCandidate,
  fetchGetVotes,
  processVoteTotals,
  UnsplitVotes,
  unsplitVotes,
  UserVotes,
} from './useElectionsServer'
import { ElectionData, useGetNominationData } from './useGovernance'
import useSpacesAuth from './useSpacesAuth'

export type ElectionsHook = {
  usedVoteBalance: ProcessedBalance
  remainingVoteBalance: ProcessedBalance
  userVotes: UserVotes[]
  userUnsplitVotes: UnsplitVotes[]
  userVoteTotals: Record<string, ProcessedBalance>
  candidateVoteTotals: Record<string, ProcessedBalance>
  refetchData: () => void
  isLoading: boolean
  error: CustomError
}

export const EmptyElectionsHook: ElectionsHook = {
  usedVoteBalance: emptyBalance,
  remainingVoteBalance: emptyBalance,
  userVotes: [],
  userUnsplitVotes: [],
  userVoteTotals: {},
  candidateVoteTotals: {},
  refetchData: () => {},
  isLoading: false,
  error: { isError: false, call: '', msg: undefined },
}

const useElections = (): ElectionsHook => {
  const { address } = useAuth()
  const { token } = useSpacesAuth()
  const [refetchCount, setRefetchCount] = useState<number>(0)

  // Get current election
  const {
    value: currentElection,
    isLoading: isLoadingCurrentElection,
    error: errorCurrentElection,
  } = useCustomContractRead(
    {
      chainId: l1NativeWagmiConfig.id,
      address: ELECTIONS_ADDRESS,
      args: [],
      abi: OMMAElection,
      functionName: 'currentElection',
    },
    refetchCount
  )
  const electionData = useMemo(() => {
    let data: ElectionData = {
      idx: 0,
      nominationTime: 0,
      campaignTime: 0,
      votingTime: 0,
      endTime: 0,
    }
    if (currentElection) {
      data = {
        idx: parseInt(currentElection[0].toString(), 10),
        nominationTime: parseInt(currentElection[1].toString(), 10),
        campaignTime: parseInt(currentElection[2].toString(), 10),
        votingTime: parseInt(currentElection[3].toString(), 10),
        endTime: parseInt(currentElection[4].toString(), 10),
      }
    }
    return data
  }, [currentElection])

  // Get candidates
  const {
    value: candidatesRaw,
    isLoading: isLoadingCandidates,
    error: errorCandidates,
  } = useCustomContractRead(
    {
      chainId: l1NativeWagmiConfig.id,
      address: ELECTIONS_ADDRESS,
      args: [],
      abi: OMMAElection,
      functionName: 'getNominatedCandidates',
    },
    refetchCount
  )
  const candidates = useMemo(
    () => (candidatesRaw ? candidatesRaw.map(cand => cand as string) : []),
    [candidatesRaw]
  )

  // Build nomination data list
  const {
    nominationData,
    isLoading: isLoadingNominationData,
    error: errorNominationData,
  } = useGetNominationData(refetchCount, candidates)

  // User vote balance
  const {
    value: vote,
    isLoading: isLoadingVote,
    error: errorVote,
  } = useCustomContractReadBalance(
    {
      chainId: l1NativeWagmiConfig.id,
      address: VOTE_ADDRESS,
      args: [address as `0x${string}`],
      abi: Vote,
      functionName: 'balanceOf',
    },
    refetchCount,
    !!address
  )

  // Force refetch
  const refetchData = useCallback(() => {
    setRefetchCount(prev => prev + 1)
  }, [])

  // Used + Remaining Vote Balance
  const [usedVoteBalance, setUsedVoteBalance] = useState<ProcessedBalance>(emptyBalance)
  const [loadingUsedVoteBalance, setLoadingUsedVoteBalance] = useState<boolean>(false)
  const [errorUsedVoteBalance, setErrorUsedVoteBalance] = useState<CustomError>({
    isError: false,
    call: '',
    msg: undefined,
  })
  const remainingVoteBalance = useMemo(
    () => processBalance(vote.value - usedVoteBalance.value),
    [vote.value, usedVoteBalance.value]
  )

  // User votes (raw, split, with signature and proofs)
  const [userVotes, setUserVotes] = useState<UserVotes[]>([])
  // User votes (unsplit per candidate in each voting action, no signature or proofs)
  const [userUnsplitVotes, setUserUnsplitVotes] = useState<UnsplitVotes[]>([])
  // User Vote totals
  const [userVoteTotals, setUserVoteTotals] = useState<Record<string, ProcessedBalance>>({})
  const [loadingUserVotes, setLoadingUserVotes] = useState<boolean>(false)
  const [errorUserVotes, setErrorUserVotes] = useState<CustomError>({
    isError: false,
    call: '',
    msg: undefined,
  })

  // Candidate Vote Totals
  const [candidateVoteTotals, setCandidateVoteTotals] = useState<Record<string, ProcessedBalance>>(
    {}
  )
  const [loadingCandidateVoteTotals, setLoadingCandidateVoteTotals] = useState<boolean>(false)
  const [errorCandidateVoteTotals, setErrorCandidateVoteTotals] = useState<CustomError>({
    isError: false,
    call: '',
    msg: undefined,
  })

  // Fetch used votes
  const fetchUsedVotesBalance = useCallback(async () => {
    setLoadingUsedVoteBalance(true)
    setErrorUsedVoteBalance({ isError: false, call: '', msg: undefined })
    try {
      if (!token) {
        return
      }
      const voteBalanceData = await fetchBalance(token)
      setUsedVoteBalance(voteBalanceData)
    } catch (error: any) {
      console.error('Error fetching used votes balance:', error)
      setErrorUsedVoteBalance({ isError: true, call: 'fetchUsedVotesBalance', msg: error.message })
    } finally {
      setLoadingUsedVoteBalance(false)
    }
  }, [token])

  // Fetch user votes
  const fetchUserVotes = useCallback(async () => {
    setLoadingUserVotes(true)
    setErrorUserVotes({ isError: false, call: '', msg: undefined })
    try {
      if (!token || !electionData.idx) {
        return
      }
      const votesData = await fetchGetVotes(token, electionData.idx)
      setUserVotes(votesData)
      setUserUnsplitVotes(unsplitVotes(votesData))
      setUserVoteTotals(processVoteTotals(votesData))
    } catch (error: any) {
      console.error('Error fetching user votes:', error)
      setErrorUserVotes({ isError: true, call: 'fetchUserVotes', msg: error.message })
    } finally {
      setLoadingUserVotes(false)
    }
  }, [token, electionData.idx])

  // Fetch candidate vote totals
  const fetchCandidateVoteTotals = useCallback(async () => {
    setLoadingCandidateVoteTotals(true)
    setErrorCandidateVoteTotals({ isError: false, call: '', msg: undefined })
    try {
      const candidatesList = nominationData
        .filter(nomination => nomination.status === 'ACCEPTED' || nomination.status === 'REMOVED')
        .map(nomination => nomination.nominee)
      const votesData = await Promise.all(
        candidatesList.map(candidate => fetchCandidate(candidate))
      )
      const votesRecord = candidatesList.reduce<Record<string, ProcessedBalance>>(
        (acc, candidate, index) => {
          acc[candidate] = votesData[index]
          return acc
        },
        {}
      )
      setCandidateVoteTotals(votesRecord)
    } catch (error: any) {
      console.error('Error fetching candidate vote totals:', error)
      setErrorCandidateVoteTotals({
        isError: true,
        call: 'fetchCandidateVoteTotals',
        msg: error.message,
      })
    } finally {
      setLoadingCandidateVoteTotals(false)
    }
  }, [nominationData])

  // Loading
  const isLoading = useMemo(
    () =>
      isLoadingCurrentElection ||
      isLoadingCandidates ||
      isLoadingNominationData ||
      isLoadingVote ||
      loadingUsedVoteBalance ||
      loadingUserVotes ||
      loadingCandidateVoteTotals,
    [
      isLoadingCurrentElection,
      isLoadingCandidates,
      isLoadingNominationData,
      isLoadingVote,
      loadingUsedVoteBalance,
      loadingUserVotes,
      loadingCandidateVoteTotals,
    ]
  )

  // Error
  const errors = [
    errorCurrentElection,
    errorCandidates,
    errorNominationData,
    errorVote,
    errorUsedVoteBalance,
    errorUserVotes,
    errorCandidateVoteTotals,
  ]
  const error = useMemo(() => parseError(errors), errors)

  // Refetch all data when refetch
  useEffect(() => {
    if (nominationData.length === 0) {
      return
    }
    console.log('refetching candidate vote totals')
    fetchCandidateVoteTotals()
  }, [refetchCount, nominationData])

  useEffect(() => {
    if (!token) {
      return
    }
    console.log('refetching used votes balance')
    fetchUsedVotesBalance()

    if (!electionData.idx) {
      return
    }
    console.log('refetching user votes')
    fetchUserVotes()
  }, [refetchCount, token, electionData.idx])

  return {
    usedVoteBalance,
    remainingVoteBalance,
    userVotes,
    userUnsplitVotes,
    userVoteTotals,
    candidateVoteTotals,
    refetchData,
    isLoading,
    error,
  }
}

export default useElections
